import { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Spinner, EmptyState } from '@/shared/ui/Spinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useProjects } from './projects.api';
import { ProjectFormDialog } from './ProjectFormDialog';
import { ProjectDetailDialog } from './ProjectDetailDialog';
import type { ProjectStatus } from '@/shared/api/types';

const STATUS_FILTER: { value: ProjectStatus | ''; label: string }[] = [
  { value: '', label: 'Alle Status' },
  { value: 'DRAFT', label: 'Entwurf' },
  { value: 'SUBMITTED', label: 'Eingegangen' },
  { value: 'REVIEW_REQUIRED', label: 'Prüfung nötig' },
  { value: 'READY_TO_SEND', label: 'Bereit' },
  { value: 'AWAITING_CUSTOMER', label: 'Wartet auf Kunde' },
  { value: 'CUSTOMER_CONFIRMED', label: 'Bestätigt' },
  { value: 'CUSTOMER_REJECTED', label: 'Abgelehnt' },
  { value: 'CLOSED', label: 'Abgeschlossen' },
];

export function ProjectsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<ProjectStatus | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const { data, isLoading } = useProjects({ q, status });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Aufträge</h1>
        <Button onClick={() => setFormOpen(true)}>
          <Plus className="h-4 w-4" /> Neuer Auftrag
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Select value={status} onChange={(e) => setStatus(e.target.value as ProjectStatus | '')} className="sm:w-52">
          {STATUS_FILTER.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
      </div>

      {isLoading ? (
        <Spinner />
      ) : !data || data.items.length === 0 ? (
        <EmptyState title="Keine Aufträge" hint="Legen Sie den ersten Auftrag an." />
      ) : (
        <ul className="space-y-2">
          {data.items.map((p) => (
            <li
              key={p.id}
              onClick={() => setDetailId(p.id)}
              className="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-brand-300 hover:shadow-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="truncate font-medium text-slate-900">{p.title}</p>
                <StatusBadge status={p.status} />
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
                <span>{p.customer.companyName}</span>
                <span>{p.object.name}</span>
                {p._count && <span>{p._count.fieldReports} Meldung(en)</span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {data && <p className="mt-3 text-xs text-slate-400">{data.total} Aufträge</p>}

      <ProjectFormDialog open={formOpen} onClose={() => setFormOpen(false)} />
      <ProjectDetailDialog projectId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
